import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { EnvironmentLoaderService } from './environment-loader.service';
import {
  ChargingStationLocation,
  ChargingStationLocationDetails,
  ChargingStationLocationSearchEvent,
  CreateChargingStationLocation,
  GetConnectorsOnLocationResponse,
  UpdateAccessGroupsOnChargingStationLocationConnectorsRequest,
  UpdateChargingStationLocationRequest,
} from 'src/app/features/charging-station-location/charging-station-location-model';
import { PagedResultV2 } from '../models/paging';
import {
  AssignPriceRegulationToLocationRequest,
  AvailablePriceRegulations,
  LocationPriceRegulationResponse,
  EditAccessGroupsRequest,
  PriceRegulationAccessGroupsResponse,
} from 'src/app/features/charging-station-location/charging-station-location-price-regulation.model';

export interface DynamicPricePolicyEligibilityResponse {
  can_have_dynamic_price_policy: boolean;
}

@Service()
export class ChargingStationLocationService {
  private environmentLoaderService = inject(EnvironmentLoaderService);
  private httpClient = inject(HttpClient);

  apiURL: string;
  apiV1URL: string;
  apiV2URL: string;
  apiLocationsURL: string;

  constructor() {
    this.apiURL = `${this.environmentLoaderService.config['serviceBaseUrl']}/api/v1/admin/ChargingStationLocation`;
    this.apiV1URL = `${this.environmentLoaderService.config['serviceBaseUrl']}/api/v1/admin/charging-station-locations`;
    this.apiV2URL = `${this.environmentLoaderService.config['serviceBaseUrl']}/api/v2/admin/charging-station-locations`;
    this.apiLocationsURL = `${this.environmentLoaderService.config['serviceBaseUrl']}/api/v1/admin/locations`;
  }

  public getConnectorsOnLocation(locationId: string): Observable<GetConnectorsOnLocationResponse> {
    return this.httpClient.get<GetConnectorsOnLocationResponse>(`${this.apiURL}/${locationId}/connectors`);
  }

  public getChargingStationLocationDetails(identifier: string): Observable<ChargingStationLocationDetails> {
    return this.httpClient.get<ChargingStationLocationDetails>(`${this.apiV2URL}/${identifier}/details`);
  }

  public setPricePolicy(id: string, chargingStationLocationIdentifier: string) {
    const request = {
      pricePolicyId: id,
    };
    return this.httpClient.put(`${this.apiURL}/${chargingStationLocationIdentifier}/PricePolicy`, request);
  }

  public create(request: CreateChargingStationLocation) {
    return this.httpClient.post(`${this.apiV2URL}`, request);
  }

  public update(identifier: string, request: UpdateChargingStationLocationRequest): Observable<void> {
    return this.httpClient.put<void>(`${this.apiV1URL}/${identifier}`, request);
  }

  public updateAccessGroupsOnChargingStationLocationConnectors({
    chargingStationLocationIdentifier,
    accessGroupIdentifiers,
  }: UpdateAccessGroupsOnChargingStationLocationConnectorsRequest) {
    return this.httpClient.put(`${this.apiURL}/${chargingStationLocationIdentifier}/access-groups`, {
      accessGroupIdentifiers,
    });
  }

  public getChargingStationLocations(searchEvent: ChargingStationLocationSearchEvent): Observable<PagedResultV2<ChargingStationLocation>> {
    const url = this.apiV2URL;
    let params = new HttpParams();

    if (searchEvent?.organizationId) {
      params = params.append('organization_id', searchEvent.organizationId);
    }
    if (searchEvent?.departmentId) {
      params = params.append('department_id', searchEvent.departmentId);
    }
    if (searchEvent?.locationsWithErrorsInSetup) {
      params = params.append('locations_with_errors_in_setup', searchEvent.locationsWithErrorsInSetup);
    }
    if (searchEvent?.query) {
      params = params.append('query', searchEvent.query);
    }
    if (searchEvent?.chargingStationLocationIdentifier) {
      params = params.append('charging_station_location_identifier', searchEvent.chargingStationLocationIdentifier);
    }
    if (searchEvent?.placeholderIdentifier) {
      params = params.append('placeholder_identifier', searchEvent.placeholderIdentifier);
    }
    if (searchEvent?.pricePolicyId) {
      params = params.append('price_policy_id', searchEvent.pricePolicyId);
    }
    if (searchEvent?.priceRegulationIdentifier) {
      params = params.append('price_regulation_identifier', searchEvent.priceRegulationIdentifier);
    }
    if (searchEvent?.accessGroupIdentifier) {
      params = params.append('access_group_identifier', searchEvent.accessGroupIdentifier);
    }
    if (searchEvent.paging?.pageIndex) {
      params = params.append('page', searchEvent.paging.pageIndex);
    }
    if (searchEvent.paging?.pageSize) {
      params = params.append('page_size', searchEvent.paging.pageSize);
    }
    if (searchEvent.sorting?.active) {
      params = params.append('sort_by', searchEvent.sorting.active);
    }
    if (searchEvent.sorting?.direction) {
      params = params.append('sort_direction', searchEvent.sorting.direction.toUpperCase());
    }

    return this.httpClient.get<PagedResultV2<ChargingStationLocation>>(url, {
      params,
    });
  }

  public getChargingStationLocationsWithAccessGroup(
    searchEvent: ChargingStationLocationSearchEvent
  ): Observable<PagedResultV2<ChargingStationLocation>> {
    let params = new HttpParams().append('hasAccessGroup', true);

    if (searchEvent?.organizationId) {
      params = params.append('organizationId', searchEvent.organizationId);
    }
    if (searchEvent?.query) {
      params = params.append('query', searchEvent.query);
    }
    if (searchEvent.paging?.pageIndex) {
      params = params.append('page', searchEvent.paging.pageIndex);
    }
    if (searchEvent.paging?.pageSize) {
      params = params.append('pageSize', searchEvent.paging.pageSize);
    }

    return this.httpClient.get<PagedResultV2<ChargingStationLocation>>(this.apiURL, { params });
  }

  public getDynamicPricePolicyEligibility(
    road: string,
    number: number,
    postalCode: string
  ): Observable<DynamicPricePolicyEligibilityResponse> {
    const params = new HttpParams().set('road', road).set('number', number).set('postal_code', postalCode);
    return this.httpClient.get<DynamicPricePolicyEligibilityResponse>(`${this.apiLocationsURL}/dynamic-price-policy-eligibility`, {
      params,
    });
  }

  // Returns the price regulations currently assigned to the given location.
  public getPriceRegulationByIdentifier(locationIdentifier: string): Observable<LocationPriceRegulationResponse> {
    const url = `${this.apiLocationsURL}/${locationIdentifier}/price-regulations`;
    return this.httpClient.get<LocationPriceRegulationResponse>(url);
  }

  // Returns the access groups of the owning organization that are still available for assignment on the location.
  public getPriceRegulationAccessGroups(locationIdentifier: string): Observable<PriceRegulationAccessGroupsResponse> {
    const url = `${this.apiLocationsURL}/${locationIdentifier}/access-groups/available`;
    return this.httpClient.get<PriceRegulationAccessGroupsResponse>(url);
  }

  // Returns price regulations available for the given location.
  public getAvailablePriceRegulations(locationIdentifier: string): Observable<AvailablePriceRegulations> {
    const url = `${this.apiLocationsURL}/${locationIdentifier}/price-regulations/available`;
    return this.httpClient.get<AvailablePriceRegulations>(url);
  }

  // Assigns a price regulation to a location for the given access groups.
  public assignPriceRegulationToLocation(
    locationIdentifier: string,
    priceRegulationIdentifier: string,
    accessGroupIdentifiers: string[]
  ): Observable<AssignPriceRegulationToLocationRequest> {
    const url = `${this.apiLocationsURL}/${locationIdentifier}/price-regulations`;
    const request: AssignPriceRegulationToLocationRequest = {
      price_regulation_identifier: priceRegulationIdentifier,
      access_group_identifiers: accessGroupIdentifiers,
    };
    return this.httpClient.post<AssignPriceRegulationToLocationRequest>(url, request);
  }

  // Edits the access groups for a price regulation on a location.
  public editAccessGroups(
    locationIdentifier: string,
    priceRegulationIdentifier: string,
    accessGroupIdentifiers: string[]
  ): Observable<LocationPriceRegulationResponse> {
    const url = `${this.apiLocationsURL}/${locationIdentifier}/price-regulations/${priceRegulationIdentifier}`;
    const request: EditAccessGroupsRequest = {
      access_group_identifiers: accessGroupIdentifiers,
    };
    return this.httpClient.put<LocationPriceRegulationResponse>(url, request);
  }

  // Deletes the relation between a location and a price regulation.
  public deletePriceRegulationRelation(
    locationIdentifier: string,
    priceRegulationIdentifier: string
  ): Observable<LocationPriceRegulationResponse> {
    const url = `${this.apiLocationsURL}/${locationIdentifier}/price-regulations/${priceRegulationIdentifier}`;
    return this.httpClient.delete<LocationPriceRegulationResponse>(url);
  }
}
